/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, Fragment, useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Drawer } from "antd"
import { Button, Form, Icon, Input } from "grab-ui"
import { useTranslationsMap } from "common/utils/i18n"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { useIsMartMenuGroup } from "pages/menu-groups"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import { getGrabXValueFromLocalStorage } from "grabx/getter"
import { MerchantPortalFeatureFlag } from "constants/features"
import { debounce } from "lodash"
import { SelectableList } from "./SelectableList"
import { drawerTitleStyle, drawerFooter } from "../styles"
import {
  selectMenuDrawerFormContainer,
  selectMenuDrawerMenuConfirmButton,
  selectMenuDrawerMenuIDFormItem,
  selectMenuDrawerMenuListContainer,
  selectMenuDrawerSearchBarContainer,
} from "./styles"

export interface SelectMenuDrawerProps extends FormComponentProps {
  visible: boolean
  onClose: () => void
  onClickCreate: (storeID: string) => void
}

export const SelectMenuDrawerComponent: FC<SelectMenuDrawerProps> = ({
  visible,
  onClose,
  onClickCreate,
  form,
}: SelectMenuDrawerProps) => {
  const dispatch = useDispatch()

  const {
    menu: { menuGroups, merchantGroupID },
  } = useSelector((state: AppState) => state)
  const isMartMenuGroup = useIsMartMenuGroup()

  const [search, setSearch] = useState("")

  const { getFieldDecorator, getFieldValue, setFieldsValue } = form
  const textAsBizType = isMartMenuGroup ? "forCatalogue" : "forMenu"

  const textMap = useTranslationsMap({
    title: `menu.menuGroup.create.${textAsBizType}.chooseExistingMenu`,
    searchPlaceholder: `menu.menuGroup.create.${textAsBizType}.enterMenuName`,
    confirm: "menu.menuGroup.create.continue",
  })

  const fetchMenuGroupList = async () => {
    const t0 = performance.now()
    const resp = await dispatch(
      actionCreator.menu.fetchMenuGroupList({
        merchantGroupID,
      }),
    )
    const t1 = performance.now()
    let labels = {
      MERCHANT_GROUP_ID: merchantGroupID,
      LOAD_TIME: t1 - t0,
      LOADING_STATUS: "SUCCESS",
      REASON: "",
      ERROR_MSG: "",
    }
    if (resp?.type?.endsWith("_FAIL")) {
      labels = {
        ...labels,
        LOADING_STATUS: "FAILURE",
        ...getResponseErrorEventLabels(resp),
      }
    }
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupCreationExistingMenu,
      action: EventAction.pageView,
      labels,
    })
  }

  useEffect(() => {
    if (visible && merchantGroupID) {
      fetchMenuGroupList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, merchantGroupID])

  const handleChangeSelectedMenu = (menuGroupID: string) => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupCreationExistingMenu,
      action: EventAction.selectMenuName,
      labels: {
        MERCHANT_GROUP_ID: merchantGroupID,
        MENU_GROUP_ID: menuGroupID,
      },
    })
    setFieldsValue({
      menuGroupID,
    })
  }

  const enableSelectMenuGroupSearch = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.enableSelectMenuGroupSearch,
  )

  const debouncedFetch = useCallback(
    debounce((merchantGroupName: string) => {
      dispatch(
        actionCreator.menu.fetchMenuGroupList({
          merchantGroupID,
          menuGroupName: merchantGroupName,
        }),
      )
    }, DEFAULT_DEBOUNCE_WAIT_TIME),
    [],
  )

  useEffect(() => {
    // TODO: Remove the 'grabx' feature flag after it's proven to handle the issue for merchants with large menu group lists
    if (!enableSelectMenuGroupSearch) return

    debouncedFetch(search)
  }, [debouncedFetch, dispatch, enableSelectMenuGroupSearch, search])

  const handleClickConfirm = () => {
    const menuGroupID = getFieldValue("menuGroupID")
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupCreationExistingMenu,
      action: EventAction.clickConfirm,
      labels: {
        MERCHANT_GROUP_ID: merchantGroupID,
        MENU_GROUP_ID: menuGroupID,
      },
    })
    onClickCreate(menuGroupID)
    onClose()
  }

  return (
    <Fragment>
      <Drawer
        title={<span css={drawerTitleStyle}>{textMap.title}</span>}
        placement="right"
        width={450}
        visible={visible}
        onClose={onClose}
        destroyOnClose
      >
        <div css={selectMenuDrawerFormContainer}>
          <div css={selectMenuDrawerSearchBarContainer}>
            <Input
              data-testid="select-menu-search-input"
              value={search}
              placeholder={textMap.searchPlaceholder}
              onChange={e => setSearch(e.target.value)}
              onClick={() => {
                TroyWebTracker.sendEvent({
                  category: EventCategory.menuGroupCreationExistingMenu,
                  action: EventAction.clickSearchMenuName,
                  labels: {
                    MERCHANT_GROUP_ID: merchantGroupID,
                  },
                })
              }}
              suffix={
                search ? (
                  <Icon
                    className="input-clear-icon"
                    type="close-circle"
                    onClick={() => setSearch("")}
                  />
                ) : (
                  <Icon type="search" />
                )
              }
            />
          </div>
          <Form.Item css={selectMenuDrawerMenuIDFormItem}>
            {getFieldDecorator("menuGroupID")(<Input />)}
          </Form.Item>
        </div>
        <div css={selectMenuDrawerMenuListContainer}>
          <SelectableList
            data-testid="select-menu-selectable-list"
            items={
              menuGroups
                ?.filter(menuGroup =>
                  menuGroup.name.toLowerCase().includes(search.toLowerCase()),
                )
                .map(menuGroup => ({
                  label: menuGroup.name,
                  value: menuGroup.menuGroupID,
                })) || []
            }
            value={getFieldValue("menuGroupID")}
            onChange={handleChangeSelectedMenu}
          />
        </div>
        <div css={drawerFooter}>
          <Button
            data-testid="select-menu-confirm-button"
            type="primary"
            css={selectMenuDrawerMenuConfirmButton}
            disabled={!getFieldValue("menuGroupID")}
            onClick={handleClickConfirm}
          >
            {textMap.confirm}
          </Button>
        </div>
      </Drawer>
    </Fragment>
  )
}
export const SelectMenuDrawer = Form.create<SelectMenuDrawerProps>({})(
  SelectMenuDrawerComponent,
)
